import React, { useState } from "react";
import { Autocomplete, Box, ListItem, Typography, Alert } from "@mui/material";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";
import { CURRENCY, MATERIAL_TYPE } from "../../utils/constants";
import { fetchRawMaterialApi } from "../../apis/rawMaterial.api";
import { FetchSKDApi } from "../../apis/skd.api";
import { FetchSFGApi } from "../../apis/sfg.api";
import { FetchFGApi } from "../../apis/FG.api";
import { FetchvendorRawMaterialWiseApi } from "../../apis/vendor.api";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { callApiAction } from "../../store/actions/commonAction";
import { useDispatch } from "react-redux";

const PurchaseOrderPlacementDialog = ({
  params,
  loading,
  onSubmit,
  onClose,
}) => {
  const dispatch = useDispatch();
  const materialVendorOptions = params?.materialVendors || [];
  const isMaterialNull = !params?.materialId;

  const currencyOptions = Object.entries(CURRENCY).map(([label, value]) => ({
    label,
    value,
  }));

  const [currency, setCurrency] = useState(null);

  const [selectedMaterial, setSelectedMaterial] = useState(
    params?.materialId
      ? {
          _id: params.materialId,
          name: params?.materialDetails?.name || "Unknown",
        }
      : null,
  );
  const [vendor, setVendor] = useState(
    params?.vendorId?._id
      ? params.vendorId
      : materialVendorOptions.find((item) => item._id === params?.vendorId) ||
          params?.vendorId ||
          null,
  );
  const [amount, setAmount] = useState(params?.amount ?? "");
  const [vendors, setVendors] = useState(materialVendorOptions);

  const getMaterialFetchFunction = () => {
    switch (params?.materialType) {
      case MATERIAL_TYPE.RAW_MATERIALS:
        return fetchRawMaterialApi;
      case MATERIAL_TYPE.SKD:
        return FetchSKDApi;
      case MATERIAL_TYPE.SFG:
        return FetchSFGApi;
      case MATERIAL_TYPE.FG:
        return FetchFGApi;
      default:
        return null;
    }
  };

  const handleMaterialChange = (changedVal) => {
    setSelectedMaterial(changedVal);

    if (changedVal && changedVal._id) {
      dispatch(
        callApiAction(
          async () =>
            await FetchvendorRawMaterialWiseApi({ materialId: changedVal._id }),
          (response) => {
            setVendors(response?.result || []);
            setVendor(null);
          },
          (err) => {
            console.error("Error fetching vendors:", err);
            setVendors([]);
          },
        ),
      );
    } else {
      setVendors([]);
    }
  };

  return (
    <CustomDialog
      id="purchase-order-placement"
      loading={loading}
      title="Place Purchase Order"
      closeText="Cancel"
      confirmText="Place Order"
      onClose={onClose}
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          vendor,
          amount,
          material: selectedMaterial,
          currency,
        });
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {isMaterialNull && (
          <Alert severity="error">
            Material doesn't exist in system. Please add material and vendor
            details before placing order.If added then select material
          </Alert>
        )}

        {/* Material Selection */}
        {isMaterialNull && params?.materialType ? (
          <AsyncDropDown
            Value={selectedMaterial}
            lazyFun={async (para) => {
              const fetchFn = getMaterialFetchFunction();
              if (fetchFn) {
                return await fetchFn({ ...para, allStatus: true });
              }
              return { result: [] };
            }}
            OptionComponent={({ option, ...rest }) => {
              return (
                <ListItem {...rest}>
                  {option?.name} {option?.mpn ? `[${option?.mpn}]` : ""}
                </ListItem>
              );
            }}
            onChange={handleMaterialChange}
            titleKey={"name"}
            valueKey={"_id"}
            InputComponent={(params) => (
              <CustomInput
                {...params}
                label="Select Material *"
                placeholder="Select Material"
                margin="dense"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        ) : null}

        <Autocomplete
          fullWidth
          options={vendors}
          value={vendor}
          onChange={(e, changedVal) => setVendor(changedVal)}
          getOptionLabel={(option) => option?.name || ""}
          isOptionEqualToValue={(option, value) => option?._id === value?._id}
          renderOption={(props, option) => (
            <ListItem {...props}>{option.name}</ListItem>
          )}
          renderInput={(params) => (
            <CustomInput
              {...params}
              label="Select Vendor *"
              placeholder="Select Vendor"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />

        <CustomInput
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          type="number"
          label="Amount *"
          min={0}
          onWheel={(e) => e.target.blur()}
        />

        {params?.materialDetails?.price_per_unit ? (
          <Typography variant="body2" color="text.secondary">
            Amount: ₹{" "}
            {Number(params?.quantity || 0) *
              Number(params?.materialDetails?.price_per_unit || 0)}
          </Typography>
        ) : null}

        <Autocomplete
          fullWidth
          options={currencyOptions}
          value={currency}
          onChange={(e, changedVal) => setCurrency(changedVal)}
          getOptionLabel={(option) => option?.label || ""}
          isOptionEqualToValue={(option, value) =>
            option?.value === value?.value
          }
          renderOption={(props, option) => (
            <ListItem {...props}>{option.label}</ListItem>
          )}
          renderInput={(params) => (
            <CustomInput
              {...params}
              label="Select Currency *"
              placeholder="Select Currency"
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
      </Box>
    </CustomDialog>
  );
};

export default PurchaseOrderPlacementDialog;
